/* eslint import/no-extraneous-dependencies: off */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import history from '@history';
import _ from '@lodash';
import { setInitialSettings } from 'app/store/fuse/settingsSlice';
import { showMessage } from 'app/store/fuse/messageSlice';
import settingsConfig from 'app/configs/settingsConfig';
import jwtService from '../auth/services/jwtService';
import { reset2FAState } from './twoFAlice';
import axios from 'axios';

export const setUser = createAsyncThunk('user/setUser', async (user, { dispatch, getState }) => {
  /*
    You can redirect the logged-in user to a specific route depending on his role
    */
  if (user.loginRedirectUrl) {
    settingsConfig.loginRedirectUrl = user.loginRedirectUrl; // for example 'apps/academy'
  }

  return user;
});

export const updateUserSettings = createAsyncThunk(
  'user/updateSettings',
  async (settings, { dispatch, getState }) => {
    const { user } = getState();
    const newUser = _.merge({}, user, { data: { settings } });

    dispatch(updateUserData(newUser));

    return newUser;
  }
);

export const updateUserShortcuts = createAsyncThunk(
  'user/updateShortucts',
  async (shortcuts, { dispatch, getState }) => {
    const { user } = getState();
    const newUser = {
      ...user,
      data: {
        ...user.data,
        shortcuts,
      },
    };

    dispatch(updateUserData(newUser));

    return newUser;
  }
);

export const logoutUser = () => async (dispatch, getState) => {
  const { user } = getState();

  if (!user.role || user.role.length === 0) {
    // is guest
    return null;
  }

  history.push({
    pathname: '/sign-in',
  });

  dispatch(setInitialSettings());
  dispatch(reset2FAState()); // Reset 2FA state on logout

  return dispatch(userLoggedOut());
};

export const updateUserData = (user) => async (dispatch, getState) => {
  if (!user.role || user.role.length === 0) {
    // is guest
    return;
  }

  jwtService
    .updateUserData(user)
    .then(() => {
      dispatch(showMessage({ message: 'User data saved with api' }));
    })
    .catch((error) => {
      dispatch(showMessage({ message: error.message }));
    });
};

export const toggleUserAuthenticator = createAsyncThunk(
  'user/2fa/toggle',
  async ({ userId, is2FaEnabled, isAccountUser }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/2fa/toggle?userId=${userId}&is2FaEnabled=${is2FaEnabled}&isAccountUser=${isAccountUser}`);
      const data = await response?.data;
      return !isAccountUser ? data?.data?.tempData?.authConfig?.authenticator || null : null;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const resetUserAuthenticator = createAsyncThunk(
  'user/2fa/reset',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/2fa/reset?userId=${userId}`);
      const data = await response?.data;
      return data?.data?.tempData?.authConfig?.authenticator || null;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const toggleUserMagicLinkAuthenticator = createAsyncThunk(
  "user/2fa/toggleUserMagicLinkAuthenticator",
  async ({ userId, is2FaMagicLinkEnabled, isAccountUser }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/2fa/toggle-magic-link`,
        {
          params: {
            userId,
            is2FaMagicLinkEnabled,
            isAccountUser
          }
        }
      );

      return !isAccountUser ? response?.data?.data?.tempData?.authConfig?.authenticator || null : null;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const toggleAuthenticatorForUsers = createAsyncThunk(
  "user/2fa/toggleAuthenticatorForUsers",
  async ({ userId, is2FaEnabledForUsers }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/2fa/toggle-for-users`,
        {
          params: {
            userId,
            is2FaEnabledForUsers
          }
        }
      );

      return response?.data?.data?.tempData?.authConfig?.authenticator || null;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const toggleRedirectMagicLinkEmails = createAsyncThunk(
  "user/2fa/toggleRedirectMagicLinkEmails",
  async ({ userId, isRedirectMagicLinkEmails }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/2fa/toggle-redirect-magic-link-emails`,
        {
          params: {
            userId,
            isRedirectMagicLinkEmails
          }
        }
      );

      return response?.data?.data?.tempData?.authConfig?.authenticator || null;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const patchAccountUsersRestrictedDomain = createAsyncThunk(
  "user/2fa/patchAccountUsersRestrictedDomain",
  async ({ userId, restrictedDomain }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/2fa/restricted-domain`,
        {
          restrictedDomain,
        },
        {
          params: {
            userId,
          },
        }
      );

      return response?.data?.data?.tempData?.authConfig?.authenticator ?? null;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const getBackupCodes = createAsyncThunk(
  'user/2fa/getBackupCodes',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(process.env.REACT_APP_API_BASE_URL + `/api/auth/2fa/backup-codes`);
      return res.data;
    } catch (err) {
      return rejectWithValue('Failed to retrieve backup codes');
    }
  }
);

export const resetBackupCodes = createAsyncThunk(
  'user/2fa/resetBackupCodes',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.post(process.env.REACT_APP_API_BASE_URL + `/api/auth/2fa/backup-codes/generate`);
      return res?.data || null;
    } catch (err) {
      return rejectWithValue('Failed to generate backup codes');
    }
  }
);


const initialState = {
  role: [], // guest
  data: {
    displayName: 'John Doe',
    photoURL: 'assets/images/avatars/brian-hughes.jpg',
    key: 'John',
    email: 'johndoe@withinpixels.com',
    shortcuts: ['apps.calendar', 'apps.mailbox', 'apps.contacts', 'apps.tasks'],
    authenticator: {}
  },
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    userLoggedOut: (state, action) => initialState,
    updateUserPhotoUrl: (state, action) => {
      state.data.photoURL = action.payload;
    },
    setAccountSetting: (state, action) => {
      state.data.displayName = action.payload.firstname + " " + action.payload.lastname;
      state.data.photoURL = action.payload.image_url;
    }
  },
  extraReducers: {
    [updateUserSettings.fulfilled]: (state, action) => action.payload,
    [updateUserShortcuts.fulfilled]: (state, action) => action.payload,
    [setUser.fulfilled]: (state, action) => action.payload,
    [toggleUserAuthenticator.fulfilled]: (state, action) => {
      if (action.payload) {
        state.data = {
          ...state.data,
          authenticator: action.payload
        };
      }
    },
    [resetUserAuthenticator.fulfilled]: (state, action) => {
      if (action.payload) {
        state.data = {
          ...state.data,
          authenticator: action.payload
        };
      }
    },
    [toggleUserMagicLinkAuthenticator.fulfilled]: (state, action) => {
      if (action.payload) {
        state.data = {
          ...state.data,
          authenticator: action.payload
        };
      }
    },
    [toggleAuthenticatorForUsers.fulfilled]: (state, action) => {
      if (action.payload) {
        state.data = {
          ...state.data,
          authenticator: action.payload
        };
      }
    },
    [toggleRedirectMagicLinkEmails.fulfilled]: (state, action) => {
      if (action.payload) {
        state.data = {
          ...state.data,
          authenticator: action.payload
        };
      }
    },
    [patchAccountUsersRestrictedDomain.fulfilled]: (state, action) => {
      if (action.payload) {
        state.data = {
          ...state.data,
          authenticator: action.payload
        };
      }
    },
    [resetBackupCodes.fulfilled]: (state, action) => {
      if (state?.data?.authenticator) {
        state.data.authenticator.isBackupCodesGenerated = true;
      }
    },
    // [toggleUserAuthenticator.pending]: (state, action) => null,
    // [toggleUserAuthenticator.fulfilled]: (state, action) => action.payload,
  },
});

export const { userLoggedOut, updateUserPhotoUrl, setAccountSetting } = userSlice.actions;

export const selectUser = ({ user }) => user;

export const selectUserShortcuts = ({ user }) => user.data.shortcuts;

export default userSlice.reducer;
